import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { PaperPlaneIcon, UploadIcon, ResetIcon } from '@radix-ui/react-icons'
import { fetcher } from 'api/swr'
import { selectFileReader } from 'commons'
import { useUI } from 'Editor'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useParams } from 'react-router-dom'
import useSWR from 'swr'
import { toast } from '@/components/ui/use-toast'
import { CptCard } from '../LayerPanel/ui'
import { SelectDlc } from './SelectDlc'
import { AutoCoverSelector } from './AutoCoverSelector'
import { CPT_IMAGE_ACCEPT, useBatchUploadDropStore } from './dropStore'
import { UploadItem } from './UploadItem'
import { UploadFile, Template } from './types'
import { enqueueCptUploads } from './uploadQueue'

type BatchUploadProps = {
  /** button：工具栏按钮；card：组件网格里的方块入口（默认） */
  triggerVariant?: 'button' | 'card'
  /** 仍挂载弹窗（供列表拖拽打开），只隐藏触发按钮 */
  hideTrigger?: boolean
}

const BatchUpload = ({
  triggerVariant = 'card',
  hideTrigger = false,
}: BatchUploadProps) => {
  const { tid } = useParams<{ tid: string }>()
  const lid = useUI((state) => state.lid)
  const { data: template } = useSWR<Template>(['/templates/' + tid], fetcher)

  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([])
  const [open, setOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pendingFiles = useBatchUploadDropStore((s) => s.pendingFiles)
  const clearPendingFiles = useBatchUploadDropStore((s) => s.clearPendingFiles)

  const onOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetSelected()
      setUploadFiles([])
    }
    setOpen(nextOpen)
  }

  const resetSelected = () => {
    setUploadFiles([])
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const resetTrim = () => {
    // 检查是否有任何文件开启了 trim
    const hasAnyTrimEnabled = uploadFiles.some((item) => item.trim)
    
    if (hasAnyTrimEnabled) {
      // 如果有开启的，全部关闭
      setUploadFiles((items) =>
        items.map((item) => ({
          ...item,
          trim: false,
          autoCover: 0,
        })),
      )
    } else {
      // 如果全部关闭，全部开启
      setUploadFiles((items) =>
        items.map((item) => ({
          ...item,
          trim: true,
        })),
      )
    }
  }

  const resetAI = () => {
    // 检查是否有任何文件开启了 AI
    const hasAnyAIEnabled = uploadFiles.some((item) => item.ai)
    
    if (hasAnyAIEnabled) {
      // 如果有开启的，全部关闭
      setUploadFiles((items) =>
        items.map((item) => ({
          ...item,
          ai: false,
        })),
      )
    } else {
      // 如果全部关闭，全部开启
      setUploadFiles((items) =>
        items.map((item) => ({
          ...item,
          ai: true,
        })),
      )
    }
  }

  const setDlc = (dlc: string) => {
    setUploadFiles((items) => items.map((item) => ({ ...item, dlc })))
  }

  const setAutoCover = (autoCover: number) => {
    setUploadFiles((items) => 
      items.map((item) => 
        // 只更新开启了 trim 的文件
        item.trim ? { ...item, autoCover } : item
      )
    )
  }



  const handleRemove = (id: string) => {
    const files = uploadFiles.filter((item) => item.id !== id)
    files.length === 0 ? onOpenChange(false) : setUploadFiles(files)
  }

  const handleChange = (id: string, key: string, value: any) => {
    setUploadFiles((items) =>
      items.map((item) => (item.id === id ? { ...item, [key]: value } : item)),
    )
  }

  const selectFile = useCallback(
    async (files: FileList | File[] | null) => {
      if (!files || files.length === 0) {
        return
      }
      setUploadFiles([])
      const filesArray = Array.from(files)
      const tasks = filesArray.map((file) => selectFileReader(file))
      const datas = (await Promise.all(tasks)).map((item) => ({
        ...item,
        trim: true,
        ai: false,
        dlc: tid!,
        autoCover: 0, // 默认不生成封面
      }))
      setUploadFiles(datas)
    },
    [tid],
  )

  useEffect(() => {
    if (!pendingFiles?.length) return
    setOpen(true)
    void selectFile(pendingFiles)
    clearPendingFiles()
  }, [pendingFiles, selectFile, clearPendingFiles])

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        toast({
          variant: 'destructive',
          title: '未识别到图片',
          description: '请拖拽 jpg / png / jpeg / webp 文件',
        })
        return
      }
      void selectFile(acceptedFiles)
    },
    [selectFile],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: CPT_IMAGE_ACCEPT,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  })

  const handleUploadAll = async () => {
    if (!tid || uploadFiles.length === 0) return
    await enqueueCptUploads(
      uploadFiles.map((item) => ({
        id: item.id,
        tid,
        lid,
        dlc: item.dlc,
        fileName: item.name,
        original: item.original,
        trim: item.trim,
        ai: item.ai,
        autoCover: item.autoCover,
        preview: item.original,
      })),
    )
    toast({
      title: '已开始后台上传',
      description: `${uploadFiles.length} 个组件正在组件选择里上传，可切层继续`,
    })
    onOpenChange(false)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {hideTrigger ? null : (
          <DialogTrigger asChild>
            {triggerVariant === 'button' ? (
              <Button
                size="sm"
                variant="outline"
                title="也可把图片拖到下方组件列表上传"
              >
                <UploadIcon className="mr-1.5 h-4 w-4" />
                上传
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                  可拖到下方列表
                </span>
              </Button>
            ) : (
              <CptCard className="aspect-square">
                <UploadIcon className="w-3/5 h-3/5 text-gray-400 group-hover:text-primary" />
              </CptCard>
            )}
          </DialogTrigger>
        )}
        <DialogContent
          className="max-w-screen-xl max-h-screen overflow-y-auto"
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Upload cpts</DialogTitle>
            <DialogDescription>
              select cpts for upload. <br />
              accept: jpg, png, jpeg, webp; <br />
              require:
              <span className="text-destructive font-bold">
                {template?.ow} x {template?.oh}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col space-y-4 py-1">
            <div className="flex w-full flex-wrap items-center gap-2">
              <div
                {...getRootProps()}
                className={cn(
                  'flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3',
                  isDragActive && 'border-primary bg-primary/5',
                )}
              >
                <input {...getInputProps()} className="hidden" />
                <Input
                  id="picture"
                  type="file"
                  multiple
                  ref={fileInputRef}
                  accept=".jpg, .png, .jpeg, .webp"
                  className="h-auto w-auto border-0 p-0 shadow-none focus-visible:ring-0 file:mr-2 file:py-0 file:leading-none"
                  onChange={(e) => selectFile(e.target.files)}
                />
                <span className="shrink-0 text-xs leading-none text-muted-foreground">
                  {isDragActive ? '松开上传' : '可拖拽上传'}
                </span>
              </div>
              {uploadFiles.length > 0 && (
                <>
                  <div className="flex-1" />
                  <div className="flex flex-wrap items-center gap-2">
                    <SelectDlc tid={tid!} update={(e) => setDlc(e)} />
                    <Button variant="outline" onClick={resetTrim}>
                      Toggle Trim
                    </Button>
                    <AutoCoverSelector update={(e) => setAutoCover(e)} />
                    <Button variant="outline" onClick={resetAI}>
                      Toggle AI
                    </Button>
                    <Button variant="outline" onClick={resetSelected}>
                      <ResetIcon className="mr-2 h-4 w-4" />
                      Reset
                    </Button>
                  </div>
                </>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Cpt</TableHead>
                  <TableHead>Info</TableHead>
                  <TableHead>DLC</TableHead>
                  <TableHead>Trim</TableHead>
                  <TableHead>Cover</TableHead>
                  <TableHead>AI</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uploadFiles.map((cpt, idx) => (
                  <UploadItem
                    key={cpt.id}
                    idx={idx}
                    cpt={cpt}
                    isBatchMutating={false}
                    handleChange={handleChange}
                    handleRemove={handleRemove}
                    template={template!}
                  />
                ))}
              </TableBody>
            </Table>
            <div className="text-end">
              {uploadFiles.length > 0 && (
                <Button variant="default" onClick={handleUploadAll}>
                  <PaperPlaneIcon className="mr-2 h-4 w-4 -rotate-45" />
                  Upload All
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default BatchUpload
